import { useState } from "react";
import Dropdown from "../../../shared/components/Dropdown/Dropdown";
import BackwardIcon from "../../../shared/icons/BackwardIcon";
import CheckMarkIcon from "../../../shared/icons/CheckMarkIcon";
import ForwardIcon from "../../../shared/icons/ForwardIcon";
import FullScreenIcon from "../../../shared/icons/FullScreenIcon";
import MuteIcon from "../../../shared/icons/MuteIcon";
import PauseIcon from "../../../shared/icons/PauseIcon";
import PlayIcon from "../../../shared/icons/PlayIcon";
import SpeedControlIcon from "../../../shared/icons/SpeedControlIcon";
import VolumeIcon from "../../../shared/icons/VolumeIcon";
import {
  PLAYBACK_RATE_OPTIONS,
  handleFastForward,
  handleFullscreen,
  handleProgressBarClick,
  handleRewind,
} from "../BoxVideo.methods";
import {
  StyledControlBar,
  StyledSpeedControlDropdown,
} from "../BoxVideo.styled";
import VerticalSlider from "@ecp-boxes/shared/components/VerticalSlider/VerticalSlider";
import { IControlBarProps } from "../BoxVideo.types";

const ControlBar: React.FC<IControlBarProps> = ({
  progress,
  playerRef,
  containerRef,
  playbackControls: {
    isPlaying,
    setIsPlaying,
    playbackRate,
    setPlaybackRate,
    isMuted,
    setIsMuted,
    setVolume,
    volume,
  },
}) => {
  const [isSpeedControlOpen, setIsSpeedControlOpen] = useState(false);
  const [isVolumeSliderOpen, setIsVolumeSliderOpen] = useState(false);

  return (
    <StyledControlBar
      progress={progress}
      className="control-bar"
      data-testid="control-bar"
    >
      {isPlaying ? (
        <PauseIcon
          onClick={() => setIsPlaying((prev) => !prev)}
          className="control-bar__pause-icon"
          data-testid="pause-icon"
        />
      ) : (
        <PlayIcon
          onClick={() => setIsPlaying((prev) => !prev)}
          className="control-bar__play-icon"
          data-testid="play-icon"
        />
      )}

      <Dropdown
        isOpen={isSpeedControlOpen}
        setIsOpen={setIsSpeedControlOpen}
        onBackDropClick={() => setIsSpeedControlOpen(false)}
        buttonContent={
          <SpeedControlIcon
            onClick={() => setIsSpeedControlOpen(true)}
            className="control-bar__dropdown-icon"
          />
        }
        selectContainerContent={
          <StyledSpeedControlDropdown className="control-bar__playback-rate-dropdown">
            {Object.values(PLAYBACK_RATE_OPTIONS).map((playbackRateOption) => {
              return (
                <p
                  key={playbackRateOption}
                  className="control-bar__playback-rate-dropdown__option"
                  data-testid="dropdown-rate-option"
                  onClick={() => {
                    setPlaybackRate(playbackRateOption);
                    setIsSpeedControlOpen(false);
                  }}
                >
                  {playbackRateOption}
                  {playbackRateOption === playbackRate && (
                    <CheckMarkIcon fill="#4286F4" />
                  )}
                </p>
              );
            })}
          </StyledSpeedControlDropdown>
        }
      />

      <BackwardIcon
        onClick={() => handleRewind(playerRef)}
        className="control-bar__rewind"
        data-testid="backward-icon"
      />
      <ForwardIcon
        onClick={() => handleFastForward(playerRef)}
        className="control-bar__fast-forward"
        data-testid="forward-icon"
      />

      <div
        className="control-bar__progress-bar"
        data-testid="progress-bar"
        onClick={(event) => handleProgressBarClick(event, playerRef)}
      >
        <div className="control-bar__progress-bar__progress" />
      </div>

      <div
        className="control-bar__volume-container"
        onMouseOver={() => setIsVolumeSliderOpen(true)}
        onMouseOut={() => setIsVolumeSliderOpen(false)}
      >
        {isMuted ? (
          <MuteIcon
            className="control-bar__volume-container__mute-icon"
            onClick={() => setIsMuted((prev) => !prev)}
            data-testid="mute-icon"
          />
        ) : (
          <>
            <VolumeIcon
              className="control-bar__volume-container__volume-icon"
              onClick={() => setIsMuted((prev) => !prev)}
              data-testid="volume-icon"
            />
            {isVolumeSliderOpen && (
              <div className="control-bar__volume-container__volume-slider">
                <VerticalSlider
                  value={volume * 100}
                  onChange={(e) => setVolume(parseFloat(e.target.value) / 100)}
                />
              </div>
            )}
          </>
        )}
      </div>
      <FullScreenIcon
        className="control-bar__full-screen"
        onClick={() => handleFullscreen(containerRef)}
        data-testid="full-screen-icon"
      />
    </StyledControlBar>
  );
};

export default ControlBar;
